// Feature #284 / #300 — the assessment form (self + peer share this
// component; the mode is derived from whether the subject is the signed-in
// user). The assessor sees the subject's current rating per skill before
// submitting — leaving it unchanged is "agree with current" (R2, no delta).
//
// Feature #300: each skill rating is chosen from an expandable ladder that
// shows the per-type rubric descriptors, not a bare 1–5 dropdown. The
// selected level (and the current rating, if different) show their full
// descriptor; other levels collapse to their label line. Ladders start
// expanded and each skill toggles independently (sticky).
//
// Bug #301: technical skills added via the picker can be removed again.
// Bug #302: unsaved work is guarded both ways — a beforeunload handler for
//   closing/refreshing the tab, and useBlocker for in-app route changes
//   (enabled by the data router in App.tsx).
// Feature #304: technical skills are added via a substring typeahead rather
//   than a flat dropdown.
// Bug #305: added technical skills keep their add-order (newest at the
//   bottom) instead of re-sorting alphabetically.
// Feature #324: the add-technical-skill control is a combobox, not a
//   search-only box — focusing it lists every available skill so the
//   assessor can browse the catalogue without knowing a name up front.
//   Typing filters that list; arrows/Enter/Escape drive it from the
//   keyboard; clicking outside closes it. The full list is already in
//   `technical_available`, so nothing new is fetched.
//
// Feature #325: the form can be saved and resumed. Only skills the assessor
//   deliberately answered are persisted — every row is pre-populated with the
//   subject's current rating (that IS "agree with current", R2), so saving
//   those too would make agreement indistinguishable from "not looked at
//   yet". `touched` is what separates them. Bug #302's unsaved-work guards
//   stay exactly as they were: saving clears `dirty`, so they now warn about
//   genuinely unsaved work rather than work the assessor has already stored.
// Feature #350: an open draft is offered for Resume before a blank form is
//   ever rendered, so a forgotten draft cannot race a fresh one.
// Feature #348: on resume, skills whose current rating moved while the draft
//   sat are flagged, and an answer that was "agree with current" must be
//   re-confirmed against the new value before the form will submit. The
//   server enforces this too — this is the humane half, not the guard.
//
// Feature #326: with `?request=<id>` the form answers a specific assessment
//   request, and three things change — all decided by the SERVER, which is
//   why this component reads them off the payload rather than deriving them:
//   * #346 the skill list is the request's scope, and the technical picker
//     is gone. R6's "a peer may introduce a skill" does not extend to an
//     ask that named exactly what it wanted.
//   * #345 `show_current_ratings: false` means the payload carries no
//     current rating anywhere, so nothing is pre-populated and there is no
//     "agree with current" to lean on. Every skill needs a real answer.
//   * #343 the request IS the permission. Nothing here needs to know that,
//     but it is why an ordinary colleague can open this page at all.
// Feature #347: every skill on a peer assessment can be declined outright —
//   "I cannot judge this" — which is recorded as its own fact and is not the
//   same statement as agreeing with the current rating. Not offered on a
//   self-assessment: you are always in a position to judge yourself.
// Feature #355: core competencies are retired from Skills Management. The
//   mandatory "Core competencies" section, its asterisks and its submit
//   guard are gone; an open-ended form is the technical section alone and
//   has no mandatory skills. The scoped-request list, which used to travel
//   in `core_competencies`, now arrives as `scoped_skills`.
// Bug #353: `?resume=<draft id>` — the link "Resume" on My Assessments and
//   "Finish it" on My Skills carry — opens the named draft directly. The
//   #350 "already in progress" question is for arriving at a form NOT
//   knowing a draft exists; asking it of someone who just clicked Resume
//   is asking them the same thing twice.
// Feature #351: the rating comment is a textarea, and on a PEER assessment
//   it is required wherever the rating changes or challenges the skill —
//   a first rating, a divergence from current, or anything at all on a
//   blind (#345) form. Agreement with the current rating (R2) is never
//   reviewed, so it needs no reason; self-assessments never need one (BA
//   decision). The server enforces the same rule; this is the readable
//   half that names the skills before the round trip.
// Feature #371: the add-technical-skill picker lives in an action bar pinned
//   to the bottom of the scroll area alongside Submit / Save draft. It used
//   to sit below the list, so every add (an expanded ladder) pushed it off
//   screen. An added skill is scrolled into view, briefly highlighted and
//   takes focus, and the picker's list opens upwards from the bar.
//   Also: submitting no longer trips the #302 "unsaved changes" prompt. The
//   router evaluates the blocker from the last render, where `dirty` was
//   still true, so `leavingRef` records the submission synchronously.
// Feature #374: a skill the catalogue lacks can be requested from the pinned
//   bar (and from the picker's "no match" state, pre-filled with what was
//   typed) without leaving the form — a modal, so the assessment's state is
//   untouched. The request goes to the sm_admin Skill Requests queue. The
//   caller's requests lodged from THIS person's form show in a banner: in
//   review, or accepted with a one-click add once the skill is in the picker
//   — which it is on any reload, because `technical_available` is fetched
//   fresh. Not offered on a scoped request (#346): the picker is gone there
//   too, because the assessor may not introduce skills.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  createDraft,
  discardDraft,
  getAssessmentForm,
  getMySkillRequests,
  saveDraft,
  submitAssessment,
  submitDraft,
  SmAssessmentForm,
  SmDraft,
  SmDraftItemInput,
  SmDraftReconciledItem,
  SmFormSkill,
  SmOptOutInput,
  SmRubricLevel,
  SmSkillRequest,
} from '@/api/sm'
import { RequestSkillDialog } from './components/RequestSkillDialog'
import { Toast } from '@/components/ui/Toast'

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

// #351 — mirrors PEER_COMMENT_MIN_LENGTH on the server. A required field
// with no floor collects "." and "n/a".
const COMMENT_MIN_LENGTH = 10

const AssessmentForm: React.FC = () => {
  const navigate = useNavigate()
  const { userId } = useParams<{ userId: string }>()
  const [searchParams] = useSearchParams()
  // #326 — which request this form answers, if any. Read from the URL so a
  // link out of an email, My Skills or My Assessments all land in the same
  // place, and so a reload keeps the scope.
  const requestId = searchParams.get('request')
  // #353 — the draft this link was explicitly asked to resume, if any.
  const resumeDraftId = searchParams.get('resume')
  const { user } = useAuth()
  const [form, setForm] = useState<SmAssessmentForm | null>(null)
  const [ratings, setRatings] = useState<Record<string, number>>({})
  const [comments, setComments] = useState<Record<string, string>>({})
  const [addedTechnical, setAddedTechnical] = useState<string[]>([])
  // Collapsed state per skill. Absent/false = expanded (the default), so the
  // form loads with every ladder open. Toggling one never touches another.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  // Bug #302: true once the assessor has entered anything, so we can warn on
  // window close/refresh. Cleared on submit so the redirect never prompts.
  const [dirty, setDirty] = useState(false)
  // Feature #304: current text in the add-technical-skill typeahead.
  const [addQuery, setAddQuery] = useState('')
  // Feature #324: whether the picker panel is open. Opening no longer
  // depends on there being a query — focusing the input is enough, which is
  // what makes the master list browsable.
  const [addOpen, setAddOpen] = useState(false)
  // Feature #324: index of the keyboard-highlighted option, -1 for none.
  const [addHighlight, setAddHighlight] = useState(-1)
  const addBoxRef = useRef<HTMLDivElement | null>(null)
  const addOptionRefs = useRef<Array<HTMLButtonElement | null>>([])
  // Feature #371: the skill just added from the picker, highlighted briefly
  // once it has been scrolled into view.
  const [justAdded, setJustAdded] = useState<string | null>(null)
  // Feature #371: set immediately before the post-submit redirect. A ref, not
  // state, because the blocker that redirect meets was built on the previous
  // render — `setDirty(false)` has not landed by then.
  const leavingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // #351 — true once a submit has been attempted, so missing reasons are
  // marked inline from then on rather than shouting on first render.
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // Feature #325 — the open draft, if the assessor has one. Refreshed from
  // every save so the #348 reconciliation below always reflects the server's
  // view rather than a stale copy from page load.
  const [draft, setDraft] = useState<SmDraft | null>(null)
  // Feature #350 — true while the assessor is being asked whether to resume
  // the draft or throw it away. The form itself stays hidden until they
  // choose; silently opening either one is how the two-drafts race starts.
  const [resumeChoice, setResumeChoice] = useState(false)
  // Skills the assessor deliberately answered. The distinction between this
  // and `ratings` is the whole reason drafts are safe: `ratings` is
  // pre-populated from the subject's current rating and cannot tell you
  // whether a value was chosen or merely defaulted.
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  // Feature #348 — skills whose stale-baseline warning the assessor has since
  // acted on in this session. Sent as `reconfirm` so the server re-anchors
  // the stored baseline; without it, saving would leave the answer stale.
  const [reconfirmed, setReconfirmed] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  // Feature #347 — skills the assessor has said they cannot judge, and why.
  // Kept apart from `ratings` on purpose: a decline is not a rating, and the
  // moment the two share a store somebody downstream treats one as the other.
  const [optedOut, setOptedOut] = useState<Record<string, boolean>>({})
  const [optOutReasons, setOptOutReasons] = useState<Record<string, string>>({})
  // Feature #374 — the skill-request modal, and the name it opens with
  // (what was typed into the picker when nothing matched, or blank).
  const [skillRequestOpen, setSkillRequestOpen] = useState(false)
  const [skillRequestSeed, setSkillRequestSeed] = useState('')
  // Feature #374 — the caller's own requests lodged from this person's
  // form. Drives the banner; a failed load is not worth an error, the
  // banner simply stays empty.
  const [mySkillRequests, setMySkillRequests] = useState<SmSkillRequest[]>([])
  // Feature #379 — confirmation after a skill request is lodged.
  const [toast, setToast] = useState<string | null>(null)
  const dismissToast = useCallback(() => setToast(null), [])

  const isSelf = user?.id === userId
  const assessmentType: 'self' | 'peer' = isSelf ? 'self' : 'peer'
  // #326 — the request as the SERVER resolved it. A request id in the URL
  // that has been revoked or already answered never reaches this: the form
  // endpoint 404s, so `request` being set means the grant is live.
  const request = form?.request || null
  const showCurrent = form ? form.show_current_ratings : true
  const allowOptOut = form ? form.allow_opt_out : false
  // With a request, the skills it asks about arrive in `scoped_skills` and
  // both technical lists come back empty. Empty without a request.
  const scopedSkills = form?.scoped_skills || []

  // Bug #302: browser-level guard for closing/refreshing the tab with unsaved
  // work. In-app route changes are handled separately by useBlocker below.
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  // Bug #302: block in-app route changes (e.g. clicking a nav item) while the
  // form has unsaved work. Requires the data router configured in App.tsx.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !leavingRef.current && dirty && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    const leave = window.confirm(
      'You have unsaved changes to this assessment. Leave and lose them?'
    )
    if (leave) blocker.proceed()
    else blocker.reset()
  }, [blocker])

  useEffect(() => {
    if (!userId) return
    getAssessmentForm(userId, requestId)
      .then((f) => {
        setForm(f)
        // Pre-populate with current ratings so "submit unchanged" = agree.
        // #345 — with ratings withheld there is nothing to pre-populate and
        // nothing to agree with, so every row starts empty and the assessor
        // has to give a real answer (or decline it).
        const initial: Record<string, number> = {}
        if (f.show_current_ratings) {
          const rows = [...f.scoped_skills, ...f.technical_on_profile]
          rows.forEach((s) => {
            if (s.current_rating) initial[s.skill_id] = s.current_rating
          })
        }
        setRatings(initial)
        // Feature #350 — the form endpoint returns the caller's own draft, so
        // the resume decision happens before a blank form is rendered.
        if (f.draft) {
          setDraft(f.draft)
          // Bug #353 — a link that names THIS draft is the decision already
          // made: open it. A link naming some other draft (discarded since,
          // or a stale bookmark) falls back to the question, because the
          // draft it would silently open is not the one that was asked for.
          if (resumeDraftId && resumeDraftId === f.draft.id) {
            hydrateFromDraft(f.draft, f, initial)
          } else {
            setResumeChoice(true)
          }
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load form'))
    // #374 — a scoped request has no picker, so nothing to request or add.
    if (!requestId) {
      getMySkillRequests(userId)
        .then(setMySkillRequests)
        .catch(() => setMySkillRequests([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, requestId, resumeDraftId])

  // Feature #325 — load a draft's saved answers over the pre-populated form.
  // Untouched skills keep the freshly-fetched current rating rather than
  // whatever was current when the draft was saved, which is precisely what
  // makes an untouched "agreement" safe to carry across a resume.
  //
  // `onForm` and `baseRatings` are passed in rather than read from state
  // because #353 calls this from inside the load callback, where `form`
  // and `ratings` are still the previous render's values.
  const hydrateFromDraft = (
    d: SmDraft,
    onForm: SmAssessmentForm | null = form,
    baseRatings: Record<string, number> = ratings
  ) => {
    const nextRatings: Record<string, number> = { ...baseRatings }
    const nextComments: Record<string, string> = {}
    const nextTouched: Record<string, boolean> = {}
    const nextOptedOut: Record<string, boolean> = {}
    const nextReasons: Record<string, string> = {}
    const added: string[] = []
    d.items.forEach((item) => {
      if (item.skill_unavailable) return
      if (item.opted_out) {
        nextOptedOut[item.skill_id] = true
        if (item.opt_out_reason) nextReasons[item.skill_id] = item.opt_out_reason
        nextTouched[item.skill_id] = true
      } else if (item.rating != null) {
        nextRatings[item.skill_id] = item.rating
        nextTouched[item.skill_id] = true
      }
      if (item.comment) nextComments[item.skill_id] = item.comment
      const listed =
        onForm?.scoped_skills.some((s) => s.skill_id === item.skill_id) ||
        onForm?.technical_on_profile.some((s) => s.skill_id === item.skill_id)
      if (!listed) added.push(item.skill_id)
    })
    setRatings(nextRatings)
    setComments(nextComments)
    setTouched(nextTouched)
    setOptedOut(nextOptedOut)
    setOptOutReasons(nextReasons)
    setAddedTechnical(added)
    setReconfirmed({})
    setResumeChoice(false)
    setDirty(false)
  }

  const handleDiscardDraft = async () => {
    if (!draft) return
    if (
      !window.confirm(
        'Discard the saved assessment and start again? The answers already ' +
          'saved will be deleted.'
      )
    )
      return
    try {
      await discardDraft(draft.id)
      setDraft(null)
      setResumeChoice(false)
      setTouched({})
      setReconfirmed({})
      setDirty(false)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to discard the draft')
    }
  }

  // Reconciliation for one skill, or undefined if the draft says nothing
  // about it. Cleared once the assessor acts on the warning.
  const staleFor = (skillId: string): SmDraftReconciledItem | undefined => {
    if (reconfirmed[skillId]) return undefined
    const item = draft?.items.find((i) => i.skill_id === skillId)
    return item && item.baseline_changed && !item.skill_unavailable ? item : undefined
  }

  const outstandingReconfirm = useMemo(
    () =>
      (draft?.reconfirm_skill_ids || []).filter((id) => !reconfirmed[id]),
    [draft, reconfirmed]
  )

  // The draft payload: deliberate answers only, plus technical skills added to
  // the form but not yet rated (so they survive the resume) and comment-only
  // rows. Everything else is derived server-side at submit time from the
  // subject's live rating.
  const buildDraftItems = (): SmDraftItemInput[] => {
    const ids = new Set<string>([
      ...Object.keys(touched).filter((id) => touched[id]),
      ...addedTechnical,
      ...Object.keys(comments).filter((id) => comments[id]),
      ...Object.keys(optedOut).filter((id) => optedOut[id]),
    ])
    return Array.from(ids).map((skill_id) => ({
      skill_id,
      // #347 — a declined skill carries no rating. Sending both would leave
      // the server guessing which the assessor meant.
      rating: optedOut[skill_id] ? null : touched[skill_id] ? ratings[skill_id] ?? null : null,
      comment: comments[skill_id] || null,
      reconfirm: !!reconfirmed[skill_id],
      opted_out: !!optedOut[skill_id],
      opt_out_reason: optedOut[skill_id] ? optOutReasons[skill_id] || null : null,
    }))
  }

  const handleSaveDraft = async () => {
    if (!form || !userId) return
    setError(null)
    setSaving(true)
    try {
      const items = buildDraftItems()
      const saved = draft
        ? await saveDraft(draft.id, items)
        : await createDraft({
            subject_user_id: userId,
            assessment_type: assessmentType,
            request_id: requestId,
            items,
          })
      setDraft(saved)
      setReconfirmed({})
      setSavedAt(saved.updated_at)
      // Bug #302's guards are about UNSAVED work. This work is now saved, so
      // clearing `dirty` keeps those warnings honest rather than removing them.
      setDirty(false)
    } catch (e: any) {
      // #350 — another tab opened a draft first. Say so plainly instead of
      // letting a bare 409 surface.
      if (e?.response?.status === 409) {
        setError(
          'An assessment for this person was started elsewhere. Reload the ' +
            'page to resume it.'
        )
      } else {
        setError(e?.response?.data?.detail || e?.message || 'Failed to save the draft')
      }
    } finally {
      setSaving(false)
    }
  }

  const availableToAdd = useMemo(
    () =>
      (form?.technical_available || []).filter((s) => !addedTechnical.includes(s.skill_id)),
    [form, addedTechnical]
  )

  // Feature #374 — what the banner has to say. In review: waiting on an
  // admin. Accepted: only while the skill is still addable here — once it is
  // on the profile or already added there is nothing left to do. An accepted
  // skill that is not in `technical_available` yet (this tab predates the
  // acceptance) is offered as a reload, since the picker is built from the
  // payload and not re-fetched.
  const pendingSkillRequests = useMemo(
    () => mySkillRequests.filter((r) => r.status === 'in_review'),
    [mySkillRequests]
  )
  const acceptedSkillRequests = useMemo(() => {
    const onForm = new Set<string>([
      ...(form?.technical_on_profile || []).map((s) => s.skill_id),
      ...addedTechnical,
    ])
    return mySkillRequests.filter(
      (r) => r.status === 'accepted' && r.created_skill_id && !onForm.has(r.created_skill_id)
    )
  }, [mySkillRequests, form, addedTechnical])
  const openSkillRequest = (seed = '') => {
    setSkillRequestSeed(seed)
    setSkillRequestOpen(true)
    setAddOpen(false)
  }

  // Feature #304 / #324 — options for the picker. An empty query is no
  // longer "no matches" but "everything available", so the panel doubles as
  // the master list. Uncapped deliberately: the panel scrolls, and silently
  // truncating a list the user opened in order to browse defeats the point.
  const addMatches = useMemo(() => {
    const q = addQuery.trim().toLowerCase()
    if (!q) return availableToAdd
    return availableToAdd.filter((s) => s.name.toLowerCase().includes(q))
  }, [availableToAdd, addQuery])

  // Feature #324 — close the picker when the click lands outside it. The
  // panel lives inside addBoxRef, so selecting an option doesn't trip this.
  useEffect(() => {
    if (!addOpen) return
    const handler = (e: MouseEvent) => {
      if (!addBoxRef.current?.contains(e.target as Node)) setAddOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addOpen])

  // Feature #324 — keep the highlight in range as the query narrows the list,
  // and scroll it into view when the keyboard moves it past the fold.
  useEffect(() => {
    setAddHighlight((h) => (h >= addMatches.length ? addMatches.length - 1 : h))
  }, [addMatches.length])
  useEffect(() => {
    if (addHighlight < 0) return
    addOptionRefs.current[addHighlight]?.scrollIntoView({ block: 'nearest' })
  }, [addHighlight])

  const setRating = (skillId: string, value: number) => {
    setRatings((prev) => ({ ...prev, [skillId]: value }))
    // Choosing a level is a deliberate answer, and it is also the act that
    // resolves a #348 staleness warning — including when they pick the same
    // number again, which is a considered decision, not a no-op.
    setTouched((prev) => ({ ...prev, [skillId]: true }))
    setReconfirmed((prev) => ({ ...prev, [skillId]: true }))
    // #347 — rating a skill you had declined is a change of mind, and the
    // rating wins. Leaving the decline set would send both.
    setOptedOut((prev) => (prev[skillId] ? { ...prev, [skillId]: false } : prev))
    setDirty(true)
  }

  // #347 — decline, or take a decline back. Declining clears the rating
  // outright rather than hiding it: an assessor who says "I cannot judge
  // this" and then submits must not have a stale number go with it.
  const toggleOptOut = (skillId: string) => {
    const next = !optedOut[skillId]
    setOptedOut((prev) => ({ ...prev, [skillId]: next }))
    setTouched((prev) => ({ ...prev, [skillId]: true }))
    if (next) {
      setRatings((prev) => {
        const copy = { ...prev }
        delete copy[skillId]
        return copy
      })
      // Declining is an answer in its own right, so it also settles any
      // outstanding #348 staleness warning on that skill.
      setReconfirmed((prev) => ({ ...prev, [skillId]: true }))
    } else {
      setOptOutReasons((prev) => {
        const copy = { ...prev }
        delete copy[skillId]
        return copy
      })
    }
    setDirty(true)
  }

  const setOptOutReason = (skillId: string, value: string) => {
    setOptOutReasons((prev) => ({ ...prev, [skillId]: value }))
    setDirty(true)
  }

  const setComment = (skillId: string, value: string) => {
    setComments((prev) => ({ ...prev, [skillId]: value }))
    setDirty(true)
  }

  // #351 — does this skill's rating need a reason? Peer only; never for a
  // decline (that has its own reason box) or an unrated row. With current
  // ratings shown, agreement with the current value (R2) is exempt — it is
  // never reviewed. Blind (#345), or a skill with no current rating, means
  // there is nothing to agree with, so any rating needs one.
  const commentRequired = (skill: SmFormSkill): boolean => {
    if (isSelf) return false
    const chosen = ratings[skill.skill_id]
    if (!chosen || optedOut[skill.skill_id]) return false
    if (!showCurrent) return true
    return skill.current_rating == null || skill.current_rating !== chosen
  }
  const commentMissing = (skill: SmFormSkill): boolean =>
    commentRequired(skill) &&
    (comments[skill.skill_id] || '').trim().length < COMMENT_MIN_LENGTH

  const toggleCollapsed = (skillId: string) =>
    setCollapsed((prev) => ({ ...prev, [skillId]: !prev[skillId] }))

  // Feature #304 — add a technical skill picked from the picker. Feature
  // #324: adding closes the panel and clears the highlight, so a second add
  // starts from a clean browse rather than a stale query.
  const addTechnical = (skillId: string) => {
    setAddedTechnical((prev) => (prev.includes(skillId) ? prev : [...prev, skillId]))
    setAddQuery('')
    setAddOpen(false)
    setAddHighlight(-1)
    setJustAdded(skillId)
    setDirty(true)
  }

  // Feature #371 — take the assessor to the skill they just added: scroll it
  // to the middle of the view (clear of the pinned bar) and focus its first
  // rating so the keyboard lands where the next decision is. The highlight
  // fades after a moment.
  useEffect(() => {
    if (!justAdded) return
    const block = document.getElementById(`skill-block-${justAdded}`)
    if (block) {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' })
      block.querySelector<HTMLElement>('[role="radio"]')?.focus({ preventScroll: true })
    }
    const t = window.setTimeout(() => setJustAdded(null), 1600)
    return () => window.clearTimeout(t)
  }, [justAdded])

  // Feature #324 — keyboard driving for the picker.
  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setAddOpen(false)
      setAddHighlight(-1)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!addOpen) {
        setAddOpen(true)
        setAddHighlight(0)
        return
      }
      if (addMatches.length === 0) return
      setAddHighlight((h) => {
        const next = e.key === 'ArrowDown' ? h + 1 : h - 1
        if (next < 0) return addMatches.length - 1
        if (next >= addMatches.length) return 0
        return next
      })
      return
    }
    if (e.key === 'Enter') {
      // Never let a stray Enter in the picker submit the surrounding form.
      e.preventDefault()
      const choice = addMatches[addHighlight]
      if (choice) addTechnical(choice.skill_id)
    }
  }

  // Bug #301 — remove a technical skill that was added by mistake, clearing
  // any rating/comment captured against it.
  const removeTechnical = (skillId: string) => {
    setAddedTechnical((prev) => prev.filter((id) => id !== skillId))
    setTouched((prev) => {
      const next = { ...prev }
      delete next[skillId]
      return next
    })
    setRatings((prev) => {
      const next = { ...prev }
      delete next[skillId]
      return next
    })
    setComments((prev) => {
      const next = { ...prev }
      delete next[skillId]
      return next
    })
    setDirty(true)
  }

  const handleSubmit = async () => {
    if (!form || !userId) return
    setError(null)

    // #346/#345 — a scoped request has to be answered in full: rated, or
    // explicitly declined. Without ratings shown there is no "leave it and
    // you agree with current" fallback, so this is the only thing standing
    // between the assessor and a 422 naming the same skills. An open-ended
    // form has no equivalent: since #355 nothing on it is mandatory.
    const unanswered = request
      ? scopedSkills.filter((s) => !ratings[s.skill_id] && !optedOut[s.skill_id])
      : []
    if (unanswered.length > 0) {
      setError(
        (allowOptOut
          ? 'Every skill on this request needs a rating, or "I cannot judge this". Still to do: '
          : 'Every skill on this request needs a rating. Still to do: ') +
          unanswered.map((s) => s.name).join(', ')
      )
      return
    }

    // Feature #348 — an answer that meant "agree with current" against a
    // value that has since moved is ambiguous, and submitting it either
    // re-proposes a rating a review just settled or records an agreement
    // never given. The server refuses these too; this is the readable half.
    if (outstandingReconfirm.length > 0) {
      const names = outstandingReconfirm
        .map((id) => draft?.items.find((i) => i.skill_id === id)?.skill_name || id)
        .join(', ')
      setError(
        'The current rating changed while this assessment was saved. ' +
          'Re-confirm your answer for: ' +
          names
      )
      return
    }

    const formSkillIds = [
      ...scopedSkills.map((s) => s.skill_id),
      ...form.technical_on_profile.map((s) => s.skill_id),
      ...addedTechnical,
    ]

    // #351 — every peer rating that changes or challenges a skill needs a
    // reason. The server refuses these too; this names them first and
    // marks the rows.
    const formSkills: SmFormSkill[] = [
      ...scopedSkills,
      ...form.technical_on_profile,
      ...addedTechnical
        .map((id) => (form.technical_available || []).find((s) => s.skill_id === id))
        .filter((s): s is SmFormSkill => Boolean(s)),
    ]
    const missingReason = formSkills.filter(commentMissing)
    if (missingReason.length > 0) {
      setSubmitAttempted(true)
      setError(
        `Say why for each rating that differs from the current one — at least ${COMMENT_MIN_LENGTH} characters. Still needed on: ` +
          missingReason.map((s) => s.name).join(', ')
      )
      return
    }

    const ratedSkillIds = formSkillIds.filter(
      (id) => ratings[id] && !optedOut[id]
    )
    // The server refuses a submission with nothing on it. Say so here in
    // words rather than letting a 422 land after the round trip.
    if (!draft && ratedSkillIds.length === 0 && !formSkillIds.some((id) => optedOut[id])) {
      setError(
        'Nothing to submit yet — rate at least one skill' +
          (allowOptOut ? ', or say you cannot judge one.' : '.')
      )
      return
    }
    const optOuts: SmOptOutInput[] = formSkillIds
      .filter((id) => optedOut[id])
      .map((skill_id) => ({
        skill_id,
        reason: optOutReasons[skill_id]?.trim() || null,
      }))

    setSubmitting(true)
    try {
      // With a draft open, submit through it: the server saves the final
      // state first (which is what makes #348's block enforceable), consumes
      // the draft in the same transaction, and fills untouched skills from
      // the subject's live rating rather than whatever this tab last loaded.
      const result = draft
        ? await submitDraft(draft.id, buildDraftItems())
        : await submitAssessment({
            subject_user_id: userId,
            assessment_type: assessmentType,
            request_id: requestId,
            items: ratedSkillIds.map((skill_id) => ({
              skill_id,
              rating: ratings[skill_id],
              comment: comments[skill_id] || undefined,
            })),
            opt_outs: optOuts,
          })
      setDirty(false) // submitted — don't warn on the redirect
      leavingRef.current = true // #371 — and the blocker can't see that yet
      // A requested assessor is usually an ordinary colleague with no read
      // access to the subject's profile, so sending them to /sm/staff would
      // be a 403. Their work lands under My Assessments.
      navigate(
        isSelf ? '/sm/my-skills' : request ? '/sm/assessments' : `/sm/staff/${userId}`,
        { state: { submissionResult: result } }
      )
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Submission failed')
    } finally {
      setSubmitting(false)
    }
  }

  const labelFor = (rubric: SmRubricLevel[], value?: number | null) =>
    value ? rubric.find((r) => r.value === value)?.label : undefined

  const renderLevel = (
    skill: SmFormSkill,
    level: SmRubricLevel,
    selected?: number
  ) => {
    const isSelected = selected === level.value
    const isCurrent = skill.current_rating === level.value
    const showDescriptor = isSelected || isCurrent
    return (
      <button
        key={level.value}
        type="button"
        role="radio"
        aria-checked={isSelected}
        onClick={() => setRating(skill.skill_id, level.value)}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          width: '100%',
          textAlign: 'left',
          padding: '8px 10px',
          border: isSelected
            ? '1px solid var(--color-magenta)'
            : '1px solid transparent',
          borderRadius: '6px',
          background: isSelected ? '#f3e8f1' : 'transparent',
          cursor: 'pointer',
          fontFamily: 'var(--font-family)',
          fontSize: '13px',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            marginTop: '2px',
            width: '13px',
            height: '13px',
            flexShrink: 0,
            borderRadius: '50%',
            border: isSelected
              ? '4px solid var(--color-magenta)'
              : '1px solid var(--color-light-grey)',
            background: 'var(--color-white)',
          }}
        />
        <span style={{ minWidth: '124px', fontWeight: isSelected ? 500 : 400 }}>
          {level.value} · {level.label}
        </span>
        {isCurrent && (
          <span
            style={{
              fontSize: '11px',
              color: 'var(--color-dark-grey)',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '10px',
              padding: '0 7px',
              lineHeight: '18px',
              flexShrink: 0,
            }}
          >
            current
          </span>
        )}
        {showDescriptor && level.description && (
          <span
            style={{
              fontSize: '12px',
              color: 'var(--color-dark-grey)',
              lineHeight: 1.5,
            }}
          >
            {level.description}
          </span>
        )}
      </button>
    )
  }

  const renderSkillBlock = (skill: SmFormSkill, removable = false) => {
    const typeRubric = (form?.rubric || []).filter(
      (r) => r.skill_type === skill.skill_type
    )
    const selected = ratings[skill.skill_id]
    const isCollapsed = !!collapsed[skill.skill_id]
    const currentLabel = labelFor(typeRubric, skill.current_rating)
    const selectedLabel = labelFor(typeRubric, selected)
    // Feature #348 — this skill's baseline moved while the draft was saved.
    const stale = staleFor(skill.skill_id)
    // Feature #347 — the assessor has declined this one.
    const declined = !!optedOut[skill.skill_id]
    // Feature #351 — does the rating need a reason, and is one missing?
    const reasonRequired = commentRequired(skill)
    const reasonMissing = submitAttempted && commentMissing(skill)

    return (
      <div
        key={skill.skill_id}
        id={`skill-block-${skill.skill_id}`}
        style={{
          borderBottom: '1px solid var(--color-light-grey)',
          padding: '12px 4px',
          // #371 — mark the skill that was just added.
          backgroundColor: justAdded === skill.skill_id ? '#fdf2f8' : 'transparent',
          transition: 'background-color 0.6s ease',
        }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleCollapsed(skill.skill_id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggleCollapsed(skill.skill_id)
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{ color: 'var(--color-dark-grey)', fontSize: '11px', width: '10px' }}
          >
            {isCollapsed ? '▸' : '▾'}
          </span>
          <span style={{ fontWeight: 500, flex: 1 }}>{skill.name}</span>
          {/* #345 — with ratings withheld the payload has no current value
              at all, so there is nothing honest to print here. Saying
              "not rated" would be a claim about the subject that the
              assessor was deliberately not given. */}
          {showCurrent && (
            <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
              {skill.current_rating
                ? `current: ${skill.current_rating} · ${currentLabel ?? ''}`
                : 'not rated'}
            </span>
          )}
          <span
            style={{
              fontSize: '13px',
              minWidth: '150px',
              textAlign: 'right',
              color:
                selectedLabel && !declined ? 'inherit' : 'var(--color-dark-grey)',
            }}
          >
            {declined
              ? 'cannot judge'
              : selectedLabel
                ? `your rating: ${selected} · ${selectedLabel}`
                : 'choose a rating'}
          </span>
          {removable && (
            <button
              type="button"
              aria-label={`Remove ${skill.name}`}
              title="Remove this skill"
              onClick={(e) => {
                e.stopPropagation()
                removeTechnical(skill.skill_id)
              }}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--color-dark-grey)',
                cursor: 'pointer',
                fontSize: '16px',
                lineHeight: 1,
                padding: '0 2px',
                fontFamily: 'var(--font-family)',
              }}
            >
              ×
            </button>
          )}
        </div>

        {stale && (
          <div
            style={{
              margin: '8px 0 0 20px',
              padding: '10px 12px',
              backgroundColor: stale.needs_reconfirmation ? '#fffbeb' : '#f8fafc',
              border: `1px solid ${
                stale.needs_reconfirmation ? '#fcd34d' : 'var(--color-light-grey)'
              }`,
              borderRadius: '6px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: 1, minWidth: '260px' }}>
              The current rating changed while this assessment was saved —{' '}
              <strong>
                {stale.baseline_rating ?? 'not rated'} &rarr;{' '}
                {stale.current_rating ?? 'not rated'}
              </strong>
              {stale.changed_by ? ` (via a ${stale.changed_by})` : ''}.{' '}
              {stale.needs_reconfirmation
                ? 'Your saved answer agreed with the old value, so it needs re-confirming.'
                : 'Your answer is unchanged — revise it if the move affects your view.'}
            </span>
            {stale.needs_reconfirmation && stale.rating != null && (
              <button
                type="button"
                onClick={() => setRating(skill.skill_id, stale.rating as number)}
                style={{
                  padding: '5px 12px',
                  border: '1px solid var(--color-magenta)',
                  background: 'var(--color-white)',
                  color: 'var(--color-magenta)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  flexShrink: 0,
                }}
              >
                Keep {stale.rating}
              </button>
            )}
          </div>
        )}

        {!isCollapsed && (
          <div style={{ marginTop: '8px', paddingLeft: '20px' }}>
            {skill.description && (
              <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', margin: '0 0 8px' }}>
                {skill.description}
              </p>
            )}
            {/* #347 — the ladder is dimmed rather than removed while a skill
                is declined, so taking the decline back is one click and the
                assessor can still read the descriptors while deciding. */}
            <div
              role="radiogroup"
              aria-label={`Rating for ${skill.name}`}
              style={{
                display: 'grid',
                gap: '2px',
                opacity: declined ? 0.45 : 1,
              }}
            >
              {typeRubric.map((level) => renderLevel(skill, level, selected))}
            </div>
            {allowOptOut && (
              <div style={{ marginTop: '10px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={declined}
                    onChange={() => toggleOptOut(skill.skill_id)}
                  />
                  <span>
                    I&rsquo;m not in a position to judge this
                    <span
                      style={{
                        color: 'var(--color-dark-grey)',
                        marginLeft: '6px',
                        fontSize: '12px',
                      }}
                    >
                      — recorded as a decline, not as agreement
                    </span>
                  </span>
                </label>
                {declined && (
                  <input
                    type="text"
                    placeholder="Why not? (optional — e.g. haven't worked with them on this)"
                    value={optOutReasons[skill.skill_id] || ''}
                    onChange={(e) => setOptOutReason(skill.skill_id, e.target.value)}
                    maxLength={500}
                    style={{ ...inputStyle, width: '100%', marginTop: '8px' }}
                  />
                )}
              </div>
            )}
            {/* #351 — a box shaped for a sentence or two, not a fragment.
                Required on a peer rating that changes or challenges the
                skill; the hint below says which, and why. */}
            <div style={{ marginTop: '10px' }}>
              <textarea
                aria-label={`Comment on ${skill.name}`}
                aria-required={reasonRequired}
                aria-invalid={reasonMissing}
                placeholder={
                  reasonRequired
                    ? 'Why this rating? What have you seen? (required)'
                    : 'Why this rating? What have you seen?'
                }
                value={comments[skill.skill_id] || ''}
                onChange={(e) => setComment(skill.skill_id, e.target.value)}
                rows={3}
                maxLength={2000}
                style={{
                  ...inputStyle,
                  border: reasonMissing ? '1px solid #dc2626' : inputStyle.border,
                  width: '100%',
                  resize: 'vertical',
                  lineHeight: 1.5,
                  display: 'block',
                }}
              />
              {reasonRequired && (
                <div
                  style={{
                    fontSize: '12px',
                    marginTop: '4px',
                    color: reasonMissing ? '#b91c1c' : 'var(--color-dark-grey)',
                  }}
                >
                  {reasonMissing
                    ? `A reason is needed here — at least ${COMMENT_MIN_LENGTH} characters.`
                    : !showCurrent || skill.current_rating == null
                      ? 'Required — this rating will go to a review, and the reason is what the review works from.'
                      : 'Required — this differs from the current rating and will go to a review.'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title={
          form
            ? isSelf
              ? 'Self-assessment'
              : request
                ? `Requested assessment — ${form.subject_display_name}`
                : `Peer assessment — ${form.subject_display_name}`
            : 'Assessment'
        }
      />
      <div style={{ padding: '24px', display: 'grid', gap: '32px', maxWidth: '1000px' }}>
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
        {!form && !error && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}

        {/* Feature #350 — a forgotten draft must not race a fresh one, so the
            choice is made before any form is rendered. */}
        {form && resumeChoice && draft && (
          <div
            style={{
              padding: '20px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '8px',
              display: 'grid',
              gap: '14px',
              maxWidth: '620px',
            }}
          >
            <h2 style={{ fontSize: '17px', fontWeight: 600, margin: 0 }}>
              You already have this assessment in progress
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--color-dark-grey)', margin: 0 }}>
              Saved {new Date(draft.updated_at).toLocaleString()} with{' '}
              {draft.items.filter((i) => i.rating != null).length} skill(s) answered.
              Starting again would throw that work away.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => hydrateFromDraft(draft)}
                style={{
                  padding: '9px 20px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Resume
              </button>
              <button
                type="button"
                onClick={handleDiscardDraft}
                style={{
                  padding: '9px 20px',
                  backgroundColor: 'transparent',
                  color: 'var(--color-dark-grey)',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Discard and start fresh
              </button>
            </div>
          </div>
        )}

        {form && !resumeChoice && draft && draft.unavailable_skill_ids.length > 0 && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#f8fafc',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--color-dark-grey)',
            }}
          >
            {draft.unavailable_skill_ids.length} skill(s) on this draft were retired
            from the catalogue while it was saved and have been removed. Nothing
            else on the assessment is affected.
          </div>
        )}

        {/* Feature #348 — the summary, so a change is not missed on a long
            form. Lists everything that moved, not only the blocking ones. */}
        {form && !resumeChoice && draft && draft.changed_skill_ids.length > 0 && (
          <div
            style={{
              padding: '14px 16px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              fontSize: '13px',
            }}
          >
            <strong style={{ display: 'block', marginBottom: '6px' }}>
              {draft.changed_skill_ids.length} rating(s) changed while this
              assessment was saved
            </strong>
            <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.7 }}>
              {draft.changed_skill_ids.map((id) => {
                const item = draft.items.find((i) => i.skill_id === id)
                if (!item) return null
                return (
                  <li key={id}>
                    {item.skill_name}: {item.baseline_rating ?? 'not rated'} &rarr;{' '}
                    {item.current_rating ?? 'not rated'}
                    {item.changed_by ? ` (via a ${item.changed_by})` : ''}
                    {outstandingReconfirm.includes(id) && (
                      <strong> — needs re-confirming</strong>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {/* Feature #326 — the terms of the ask, stated before the form. Who
            wants it, what it covers, by when, and whether it is blind. */}
        {form && !resumeChoice && request && (
          <div
            style={{
              padding: '16px 18px',
              border: '1px solid var(--color-light-grey)',
              borderLeft: '4px solid #2563eb',
              borderRadius: '8px',
              display: 'grid',
              gap: '8px',
              fontSize: '14px',
            }}
          >
            <strong>
              {request.requested_by_display_name || 'A Skills Manager'} asked you to
              assess {form.subject_display_name}
            </strong>
            <span style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
              {request.skills.length} skill
              {request.skills.length === 1 ? '' : 's'} to assess
              {request.due_at &&
                ` · due ${new Date(request.due_at).toLocaleDateString()}`}
            </span>
            {request.note && (
              <span style={{ fontStyle: 'italic', color: 'var(--color-dark-grey)' }}>
                “{request.note}”
              </span>
            )}
            {/* #345 — say plainly that the numbers are missing on purpose.
                Without this, a blind form reads as a page that failed to
                load the ratings. */}
            {!showCurrent && (
              <span style={{ fontSize: '13px' }}>
                You are <strong>not</strong> being shown{' '}
                {form.subject_display_name}&rsquo;s current ratings — an
                independent view has been asked for. Rate each skill as you see
                it{allowOptOut && ', or say you cannot judge it'}.
              </span>
            )}
            <span style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
              What you submit is advisory: it raises deltas for a Skills Manager
              to settle at a review with {form.subject_display_name}, and never
              changes their ratings on its own.
            </span>
          </div>
        )}

        {/* Feature #374 — the caller's skill requests for this form. */}
        {form && !resumeChoice && !request &&
          (pendingSkillRequests.length > 0 || acceptedSkillRequests.length > 0) && (
          <div
            style={{
              padding: '12px 16px',
              border: '1px solid var(--color-light-grey)',
              borderLeft: '4px solid var(--color-magenta)',
              borderRadius: '8px',
              fontSize: '13px',
              display: 'grid',
              gap: '6px',
            }}
          >
            {acceptedSkillRequests.map((r) => {
              const inPicker = (form.technical_available || []).some(
                (s) => s.skill_id === r.created_skill_id
              )
              return (
                <div
                  key={r.id}
                  style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <span style={{ flex: 1, minWidth: '240px' }}>
                    Your request for <strong>{r.created_skill_name || r.name}</strong> was
                    accepted — it is in the catalogue now.
                    {r.resolution_note && (
                      <span style={{ color: 'var(--color-dark-grey)' }}> “{r.resolution_note}”</span>
                    )}
                  </span>
                  {inPicker ? (
                    <button
                      type="button"
                      onClick={() => addTechnical(r.created_skill_id as string)}
                      style={{
                        padding: '5px 12px',
                        border: '1px solid var(--color-magenta)',
                        background: 'var(--color-white)',
                        color: 'var(--color-magenta)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      Add it to this assessment
                    </button>
                  ) : (
                    <span style={{ color: 'var(--color-dark-grey)', fontSize: '12px' }}>
                      Save your draft and reload to see it in the picker.
                    </span>
                  )}
                </div>
              )
            })}
            {pendingSkillRequests.length > 0 && (
              <span style={{ color: 'var(--color-dark-grey)' }}>
                Waiting for a Skills Administrator:{' '}
                {pendingSkillRequests.map((r) => r.name).join(', ')}. You&rsquo;ll be
                notified when {pendingSkillRequests.length === 1 ? 'it is' : 'they are'}{' '}
                reviewed.
              </span>
            )}
          </div>
        )}

        {form && !resumeChoice && (
          <>
            {/* #346 — a scoped request is exactly the skills asked about:
                no profile list, no picker. */}
            {request && (
            <section>
              <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
                Skills to assess
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
                {allowOptOut
                  ? 'Rate each one, or say you are not in a position to judge it. ' +
                    'Nothing outside this list is affected.'
                  : 'Rate each one. Nothing outside this list is affected.'}
                {/* #351 — a request is always a peer assessment. */}
                {' Each rating you give needs a reason: it is what the review works from.'}
              </p>
              <div style={{ borderTop: '1px solid var(--color-light-grey)' }}>
                {scopedSkills.map((s) => renderSkillBlock(s))}
              </div>
            </section>
            )}

            {/* The open-ended form. #355: this is the whole form now — there
                is no mandatory section above it and nothing here is
                required, beyond rating (or declining) at least one skill. */}
            {!request && (
            <section>
              <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>Technical skills</h2>
              <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
                Rate the ones you have an opinion on. Leaving a skill at its current
                rating means you still agree with it.
                {/* #351 — peer only; a self-assessment's comments stay optional. */}
                {!isSelf &&
                  ' Where you differ from the current rating, or rate a skill for the first time, say why — the reason goes to the review.'}
              </p>
              <div style={{ borderTop: '1px solid var(--color-light-grey)' }}>
                {form.technical_on_profile.length === 0 && addedTechnical.length === 0 && (
                  <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: '12px 4px' }}>
                    No technical skills on {isSelf ? 'your' : 'this'} profile yet — add one with the picker at the bottom of the page.
                  </p>
                )}
                {form.technical_on_profile.map((s) => renderSkillBlock(s))}
                {/* Bug #305: render added skills in the order they were added
                    (newest at the bottom), not the alphabetical catalogue order. */}
                {addedTechnical
                  .map((id) => (form.technical_available || []).find((s) => s.skill_id === id))
                  .filter((s): s is SmFormSkill => Boolean(s))
                  .map((s) => renderSkillBlock(s, true))}
              </div>
            </section>
            )}

            {/* Feature #371 — the action bar is pinned to the foot of the
                scroll area, so adding a skill (and the tall ladder that
                comes with it) can never push the picker or Submit out of
                view. It stays in normal flow, so at the very bottom of the
                form it covers nothing. */}
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '8px',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
              }}
            >
              {!request && availableToAdd.length > 0 && (
                <div
                  ref={addBoxRef}
                  style={{ position: 'relative', flex: '1 1 260px', maxWidth: '360px' }}
                >
                  {/* Feature #324 — combobox: browse the whole list on focus,
                      narrow it by typing. */}
                  <input
                    type="text"
                    role="combobox"
                    aria-expanded={addOpen}
                    aria-controls="add-skill-listbox"
                    aria-autocomplete="list"
                    aria-activedescendant={
                      addOpen && addHighlight >= 0 && addMatches[addHighlight]
                        ? `add-skill-option-${addMatches[addHighlight].skill_id}`
                        : undefined
                    }
                    value={addQuery}
                    onChange={(e) => {
                      setAddQuery(e.target.value)
                      setAddOpen(true)
                      setAddHighlight(-1)
                    }}
                    onFocus={() => setAddOpen(true)}
                    onClick={() => setAddOpen(true)}
                    onKeyDown={handleAddKeyDown}
                    placeholder="+ Add a technical skill — browse or search…"
                    style={{ ...inputStyle, width: '100%', paddingRight: '28px' }}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={addOpen ? 'Hide skill list' : 'Show all skills'}
                    onClick={() => setAddOpen((o) => !o)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      height: '100%',
                      width: '26px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--color-dark-grey)',
                      fontSize: '10px',
                      lineHeight: 1,
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    {addOpen ? '▲' : '▼'}
                  </button>
                  {addOpen && (
                    <div
                      id="add-skill-listbox"
                      role="listbox"
                      aria-label="Available technical skills"
                      style={{
                        position: 'absolute',
                        zIndex: 10,
                        // #371 — the picker sits in the bar at the foot of
                        // the view, so its list opens upwards.
                        bottom: '100%',
                        left: 0,
                        right: 0,
                        marginBottom: '4px',
                        background: 'var(--color-white)',
                        border: '1px solid var(--color-light-grey)',
                        borderRadius: '6px',
                        maxHeight: '260px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      }}
                    >
                      <div
                        style={{
                          padding: '6px 10px',
                          fontSize: '11px',
                          color: 'var(--color-dark-grey)',
                          borderBottom: '1px solid var(--color-light-grey)',
                          position: 'sticky',
                          top: 0,
                          background: 'var(--color-white)',
                        }}
                      >
                        {addQuery.trim()
                          ? `${addMatches.length} matching`
                          : `All ${addMatches.length} available skills`}
                      </div>
                      {addMatches.length === 0 ? (
                        <div style={{ padding: '8px 10px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                          No matching skills.{' '}
                          {/* #374 — the moment the need arises: offer the
                              request with what they typed already filled in. */}
                          <button
                            type="button"
                            onClick={() => openSkillRequest(addQuery.trim())}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              color: 'var(--color-magenta)',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontFamily: 'var(--font-family)',
                              textDecoration: 'underline',
                            }}
                          >
                            Request &ldquo;{addQuery.trim()}&rdquo; as a new skill
                          </button>
                        </div>
                      ) : (
                        addMatches.map((s, i) => (
                          <button
                            key={s.skill_id}
                            id={`add-skill-option-${s.skill_id}`}
                            ref={(el) => {
                              addOptionRefs.current[i] = el
                            }}
                            type="button"
                            role="option"
                            aria-selected={i === addHighlight}
                            onMouseEnter={() => setAddHighlight(i)}
                            onClick={() => addTechnical(s.skill_id)}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 10px',
                              border: 'none',
                              background: i === addHighlight ? '#f3e8f1' : 'transparent',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontFamily: 'var(--font-family)',
                            }}
                          >
                            {s.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* #374 — shown whether or not the picker is (the picker hides
                  once everything available has been added; the need for a
                  missing skill is if anything greater then). Never on a
                  scoped request, for the same reason the picker is not. */}
              {!request && (
                <button
                  type="button"
                  onClick={() => openSkillRequest('')}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: '0 4px',
                    color: 'var(--color-magenta)',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontFamily: 'var(--font-family)',
                    textDecoration: 'underline',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Can&rsquo;t find a skill? Request one
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || saving || outstandingReconfirm.length > 0}
                title={
                  outstandingReconfirm.length > 0
                    ? 'Re-confirm the ratings that changed before submitting'
                    : undefined
                }
                style={{
                  padding: '10px 24px',
                  backgroundColor:
                    outstandingReconfirm.length > 0
                      ? 'var(--color-light-grey)'
                      : 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor:
                    submitting || saving
                      ? 'wait'
                      : outstandingReconfirm.length > 0
                        ? 'not-allowed'
                        : 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {submitting ? 'Submitting…' : 'Submit assessment'}
              </button>
              {/* Feature #325 — explicit save. Deliberately not autosave: a
                  peer assessment is an unpublished opinion about a named
                  person, and it should be stored when the assessor decides
                  it should be, not as a side effect of typing. */}
              <button
                onClick={handleSaveDraft}
                disabled={saving || submitting}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: 'var(--color-dark-grey)',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: saving || submitting ? 'wait' : 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              {savedAt && !dirty && (
                <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                  Draft saved {new Date(savedAt).toLocaleTimeString()} — finish it
                  later from My Assessments.
                </span>
              )}
            </div>
          </>
        )}
      </div>
      {/* Feature #374 — lodged without leaving the form. */}
      {userId && (
        <RequestSkillDialog
          isOpen={skillRequestOpen}
          initialName={skillRequestSeed}
          subjectUserId={userId}
          onClose={() => setSkillRequestOpen(false)}
          onCreated={(created) => {
            setMySkillRequests((prev) => [created, ...prev])
            setSkillRequestOpen(false)
            setToast(`Skill request sent: “${created.name}”. You'll be notified once it's reviewed.`)
          }}
        />
      )}
      {/* Feature #379 — the modal closing is not an acknowledgement. */}
      <Toast message={toast} onDismiss={dismissToast} />
    </div>
  )
}

export default AssessmentForm
